import type { Partner } from "@expert/shared-types";

import { Flex, Text } from "@mantine/core";

import { useVerifiedCustomerStore } from "@/customer-info";
import { CustomerInsights } from "@/customer-panel/components/customer-insights/CustomerInsights";
import { ShieldIcon } from "@/customer-panel/components/customer-panel/ShieldIcon";
import { usePartner, useSessionId } from "@/sdk";

import { useProductOfferStore } from "../home-product/stores/useProductOfferStore";
import { EligibilityStatus } from "../home-product/types";

export function CustomerView({ hideCustomerInsights = false }: { hideCustomerInsights?: boolean }) {
    const sessionId = useSessionId();
    const partner = usePartner();
    const { getVerifiedCustomer } = useVerifiedCustomerStore();
    const { asurionAgreements: alreadyEnrolledProducts, partnerAgreements } = getVerifiedCustomer(sessionId);
    const hasHomePlus = alreadyEnrolledProducts.some((product) => product.includes("HOMEPLUS"));
    const hasAppliancePlus = alreadyEnrolledProducts.some((product) => ["APPLHOMEPLUS35_99"].includes(product));
    const { getPrimaryProducts } = useProductOfferStore();
    const primaryProducts = getPrimaryProducts(sessionId);

    //VHDP or HTP
    const alreadyEnrolled = primaryProducts
        .filter((p) => p.isPrimaryProduct && p.status === EligibilityStatus.Enrolled)
        .map((p) => p.productSku)
        .at(0);

    const { getProductInfo } = useProductOfferStore.getState();
    const showPartnerEnrollment = Boolean(alreadyEnrolled && (partner === "verizon" || partner === "att"));
    return (
        <>
            {
                <Flex direction={"column"} px={14} pt={14} gap={14}>
                    {showPartnerEnrollment && alreadyEnrolled && (
                        <Flex align={"center"}>
                            <ShieldIcon />
                            <Text pl="xs" fz="sm">
                                Enrolled in{" "}
                                <Text component="span" fw={700} fz="sm" style={{ textTransform: "capitalize" }}>
                                    {mapPartnerToDisplayName(partner)}{" "}
                                    {getProductInfo(sessionId, alreadyEnrolled)?.displayName}
                                </Text>
                            </Text>
                        </Flex>
                    )}

                    {partnerAgreements.length > 0 &&
                        partnerAgreements?.map((product, key) => (
                            <Flex key={key} align={"center"}>
                                <ShieldIcon />
                                <Text pl="xs" fz="sm">
                                    Enrolled in{" "}
                                    <Text component="span" fw={700} fz="sm" style={{ textTransform: "capitalize" }}>
                                        {mapPartnerToDisplayName(partner)} {sanitizeRetailName(product, partner)}
                                    </Text>
                                </Text>
                            </Flex>
                        ))}

                    {hasHomePlus && (
                        <Flex align={"center"}>
                            <ShieldIcon />
                            <Text pl="xs" fz="sm">
                                Enrolled in{" "}
                                <Text component="span" fw={700} fz="sm">
                                    Home+
                                </Text>
                            </Text>
                        </Flex>
                    )}

                    {hasAppliancePlus && (
                        <Flex align={"center"}>
                            <ShieldIcon />
                            <Text pl="xs" fz="sm">
                                Enrolled in{" "}
                                <Text component="span" fw={700} fz="sm">
                                    Appliance+
                                </Text>
                            </Text>
                        </Flex>
                    )}
                </Flex>
            }

            {!hideCustomerInsights && <CustomerInsights />}
        </>
    );
}

export function mapPartnerToDisplayName(partner: Partner) {
    switch (partner) {
        case "adt":
            return "ADT";
        case "att":
            return "ATT";
        case "asurionhomeplus":
            return "Asurion";
        case "fios":
            return "Verizon";
        default:
            return partner.charAt(0).toUpperCase() + partner.slice(1);
    }
}

//Makes sure that if the RetailName from CustomerAPI includes the partner at the start, we remove it
function sanitizeRetailName(name: string, partner: Partner) {
    const regex = new RegExp(`^${partner}\\s*`, "i");

    switch (partner) {
        case "att":
            return name.replace(/^(ATT|AT&T)\s*/i, "");
        default:
            return name.replace(regex, "");
    }
}
